import { type FC, type KeyboardEvent, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import type { ComposerSubmit } from "./types";
import { threadTexts } from "./texts";
import {
  composerAfterSubmit,
  composerPayload,
  showResolve,
} from "./model/composer";

interface Props {
  /** Подсказка подхвата разговора (имя зрителя и что уже сделал помощник). */
  placeholderHint: string | null;
  sending: boolean;
  /** «и решить обращение» есть, пока обращение не решено. */
  canResolve: boolean;
  /** false оставляет текст в поле (отправка не удалась). */
  onSubmit: (
    value: ComposerSubmit
  ) => Promise<boolean | undefined> | boolean | undefined;
}

type Mode = "reply" | "note";

/**
 * Поле ответа внизу карточки. Сверху выбор, кому пишем: ответ зрителю или
 * внутренняя заметка команде; в режиме заметки вся поверхность подкрашена
 * цветом предупреждения, чтобы заметку нельзя было спутать с ответом.
 */
export const ThreadComposer: FC<Props> = ({
  placeholderHint,
  sending,
  canResolve,
  onSubmit,
}) => {
  const [text, setText] = useState("");
  const [resolve, setResolve] = useState(false);
  const [mode, setMode] = useState<Mode>("reply");
  const internal = mode === "note";
  // Второй клик до того, как родитель выставит sending, не шлёт дубль.
  const inFlight = useRef(false);

  const submit = async () => {
    const state = { text, resolve, internal };
    const payload = composerPayload(state);
    if (!payload || sending || inFlight.current) return;
    inFlight.current = true;
    try {
      const next = composerAfterSubmit(state, await onSubmit(payload));
      setText(next.text);
      setResolve(next.resolve);
    } finally {
      inFlight.current = false;
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <Paper
      elevation={4}
      data-mode={mode}
      sx={(theme) => ({
        p: 2,
        border: 1,
        borderColor: internal
          ? alpha(theme.palette.warning.main, 0.6)
          : "divider",
        transition: theme.transitions.create(["border-color", "background"]),
        ...(internal && {
          backgroundImage: `linear-gradient(${alpha(
            theme.palette.warning.main,
            0.1
          )}, ${alpha(theme.palette.warning.main, 0.1)})`,
        }),
      })}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ sm: "center" }}
        sx={{ mb: 1.5 }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          color={internal ? "warning" : "primary"}
          aria-label={threadTexts.modeLabel()}
          onChange={(_, value: Mode | null) => {
            if (value) setMode(value);
          }}
          sx={{
            flexShrink: 0,
            "& .MuiToggleButton-root": { px: 1.5 },
          }}
        >
          <ToggleButton value="reply">
            <ReplyOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
            {threadTexts.modeReply()}
          </ToggleButton>
          <ToggleButton value="note">
            <LockOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
            {threadTexts.internalSwitch()}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography
          variant="caption"
          color={internal ? "warning.main" : "text.secondary"}
        >
          {internal ? threadTexts.noteHint() : threadTexts.replyHint()}
        </Typography>
      </Stack>
      <TextField
        multiline
        minRows={3}
        maxRows={12}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={sending}
        color={internal ? "warning" : "primary"}
        placeholder={
          internal
            ? threadTexts.notePlaceholder()
            : (placeholderHint ?? threadTexts.placeholder())
        }
        slotProps={{
          htmlInput: {
            "aria-label": internal
              ? threadTexts.notePlaceholder()
              : threadTexts.placeholder(),
          },
        }}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 1 }}
      >
        {showResolve({ canResolve, internal }) && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={resolve}
                onChange={(e) => setResolve(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">{threadTexts.resolve()}</Typography>
            }
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {threadTexts.hotkey()}
        </Typography>
        <Button
          variant="contained"
          disableElevation
          color={internal ? "warning" : "primary"}
          startIcon={internal ? <LockOutlinedIcon /> : <SendRoundedIcon />}
          disabled={sending || !text.trim()}
          onClick={() => void submit()}
        >
          {internal ? threadTexts.addNote() : threadTexts.send()}
        </Button>
      </Stack>
    </Paper>
  );
};
